import { ButtonWidget, ButtonType } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps, FormikValues } from 'formik';
import React = require('react')
import { LoginUrlsWrapper } from '~src/app/spaces/Login/LoginUrls';
import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/common/react/CommonStyles';
import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { LoginErrorCodes, UserErrorCode } from '~src/ts/helpers/ErrorCodes';
import { ValidationHelpers } from '~src/userManagement/helpers/ValidationHelpers';
import { LocalAuthConfiguration } from '../models/LocalAuthConfiguration';
import { IPasswordPolicy } from '../models/validation/IPasswordPolicy';
import { AuthenticationService } from '../services/AuthenticationService';
import { LoginPageStyles } from '../styles/LoginPageStyles';
import { LocalUsernameAndPasswordRateLimiting } from './LocalUsernameAndPasswordRateLimiting';

export interface ResetPasswordWithUsernameFormProps {
    username: string;
}

interface ResetPasswordFormFields {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const buttonStyle: React.CSSProperties = {
    marginTop: CommonStyles.Sizes.XLargeSpace - CommonStyles.Sizes.DefaultSpace,
};

export function ResetPasswordWithUsernameForm(props: ResetPasswordWithUsernameFormProps) {

    const [errorMessage, setErrorMessage] = React.useState('');
    const [localAuthConfiguration, setLocalAuthConfiguration] = React.useState(LocalAuthConfiguration.default());
    const [rateLimitingHandler, setRateLimitingHandler] = React.useState(() => ((error: IError, username: string) => Promise.resolve()));

    function validateOldPassword(value: string): string {
        if (!value) {
            return Messages.enterYourOldPassword();
        }
    }

    function validateOldAndNewPassword(newPassword: string, values: ResetPasswordFormFields): string {

        if (!newPassword) {
            return Messages.enterYourPassword();
        }

        if (values.oldPassword && (values.oldPassword === newPassword)) {
            return Messages.newPasswordIsSameAsOldPasswordError();
        }
    }

    function validateNewAndConfirmPassword(confirmPassword: string, values: ResetPasswordFormFields): string {

        if (confirmPassword && (values.newPassword !== confirmPassword)) {
            return Messages.passwordsDoNotMatch();
        }
    }

    function getFailedPasswordPolicies(newPassword: string): IPasswordPolicy[] {
        return localAuthConfiguration.performPasswordValidation(newPassword);
    }

    function shouldDisableButton(formik: FormikProps<ResetPasswordFormFields>): boolean {
        return !formik.values.oldPassword || !formik.values.newPassword || formik.values.confirmPassword !== formik.values.newPassword
            || getFailedPasswordPolicies(formik.values.newPassword).length > 0;

    }

    function handleFailure(error: IError): void {
        if (!error.code) {
            return null;
        }
        if (error.code === LoginErrorCodes.BadCredentials || error.code === UserErrorCode.UserInvalidPassword) {
            setErrorMessage(Messages.errorUserOldPasswordIncorrect());
        } else if (error.code === UserErrorCode.LoginAttemptRejected) {
            //rate limiting error handled by rateLimitingHandler    
        } else if (error.code === UserErrorCode.AccountIsBlocked) {
            setErrorMessage(Messages.errorAccountIsBlocked());
        } else if (error.code === UserErrorCode.PasswordNotUpdated) {
            setErrorMessage(Messages.errorPasswordNotUpdated());
        } else {
            setErrorMessage(Messages.unexpectedError());
        }
    }

    function sendPasswordResetRequest(formProps: ResetPasswordFormFields, formik: FormikHelpers<any>): void {

        AuthenticationService.sendPasswordResetRequest(props.username, formProps.oldPassword, formProps.newPassword, '')
            .then((resetPasswordResult: IResult) => {
                LoginUrlsWrapper.navigateToSignIn({
                    passwordResetSuccess: true
                });
            }, (error: IError) => {
                rateLimitingHandler(error, props.username)
                    .then(() => {
                        handleFailure(error);
                        formik.resetForm();
                        formik.setValues({
                            oldPassword: '', newPassword: '', confirmPassword: ''
                        });
                    });

            });
    }



    React.useEffect(() => {
        LocalAuthConfiguration.fetchCurrentLocalAuthConfiguration()
            .then(serverConfig => setLocalAuthConfiguration(serverConfig));
    }, []);


    return (
        <Formik
            initialValues={{ oldPassword: '', newPassword: '', confirmPassword: '' }}
            onSubmit={(values: ResetPasswordFormFields, formik: FormikHelpers<any>) => {
                sendPasswordResetRequest(values, formik);
            }}
            enableReinitialize={true}
        >
            {(formik: FormikProps<ResetPasswordFormFields>) => (
                <><div className='tb-login-msg-container'>
                    <div className='tb-login-msg tb-login-error'>
                        {Messages.passwordExpiredInfo()}
                    </div>
                </div>
                    <form name='passwordResetForm' autoComplete='off' noValidate onSubmit={formik.handleSubmit} tb-data-test-id='passwordResetForm'>
                        <div className='tb-login-text-box tb-padded'>
                            <FormikTextBox
                                name='oldPassword'
                                required={true}
                                label={Messages.oldPassword()}
                                testId='textbox-old-password'
                                type={'password'}
                                className={LoginPageStyles.textboxStyle}
                                validate={validateOldPassword}
                                showValidationMessage={true}
                                eagerValidation={true} />
                        </div>
                        <div className='tb-login-text-box tb-padded'>
                            <FormikTextBox
                                name='newPassword'
                                required={true}
                                label={Messages.newPassword()}
                                type={'password'}
                                className={LoginPageStyles.textboxStyle}
                                validate={(value: string) => validateOldAndNewPassword(value, formik.values)}
                                showValidationMessage={true}
                                eagerValidation={true}
                                testId='textbox-password' />
                        </div>
                        <div className='tb-login-text-box tb-padded'>
                            <FormikTextBox
                                name='confirmPassword'
                                required={true}
                                label={Messages.confirmPassword()}
                                validate={(value: string) => validateNewAndConfirmPassword(value, formik.values)}
                                showValidationMessage={true}
                                eagerValidation={true}
                                type={'password'}
                                className={LoginPageStyles.textboxStyle}
                                testId='textbox-confirm' />
                        </div>
                        <LocalUsernameAndPasswordRateLimiting populateHandler={(handler) => setRateLimitingHandler(() => handler)} />
                        <div className={'tb-login-msg-container'}>
                            <div className={'tb-login-msg tb-login-error'} tb-test-id="message-failure" role="alert">
                                {errorMessage}
                                {formik.values.newPassword && ValidationHelpers.createPasswordPolicyErrorMessageSectionFor(getFailedPasswordPolicies(formik.values.newPassword))}
                            </div>
                        </div>
                        <ButtonWidget buttonType={ButtonType.Outline}
                            tb-test-id="button-reset"
                            type='submit'
                            style={buttonStyle}
                            testId='button-reset'
                            disabled={shouldDisableButton(formik)}
                            className={LoginPageStyles.submitButtonStyle}>
                            {Messages.resetPassword()}
                        </ButtonWidget>
                    </form></>
            )}
        </Formik>
    );

}

